import { DateTime as dateTime, Interval } from 'luxon'
import { toLuxonDateTime } from '@fullcalendar/luxon2'
import { appendCsrfParams } from '~/common/helpers/csrf'

const store = new Map()
const pendingRequests = new Map()

let pendingRequestCount = 0
let storeVersion = 0

export const pendingCalendarEventFetchCount = () => pendingRequestCount

const dispatchLoadingEvent = (eventName) => {
  document.dispatchEvent(new CustomEvent(eventName, { bubbles: true }))
}

const incrementPendingRequestCount = () => {
  if (pendingRequestCount === 0) dispatchLoadingEvent('calendar:loadingStart')
  pendingRequestCount += 1
}

const decrementPendingRequestCount = () => {
  pendingRequestCount = Math.max(0, pendingRequestCount - 1)
  if (pendingRequestCount === 0) dispatchLoadingEvent('calendar:loadingEnd')
}

/* Returns all available slots in the local store */
export const getAvailableBookingSlots = () => {
  return Array.from(store.values())
    .flat()
    .filter((e) => e.groupId == 'available')
}

/*
  Given a start time and a duration in minutes
    - This function checks the local store for available intervals
    - Checks if a booking can be created from start_time to start_time+duration by looking up intervals
    - If it can, that's our booking slot's start time.
    - However, in some cases, there might not be enough time available in the booking slot.
    - In that case, we return the end time of the (closest available slot that starts before and ends after the given start time) minus the duration as the final start time.
*/
export const getBookingSlot = (startLuxon, durationInMinutes) => {
  const endLuxon = startLuxon.plus({ minutes: durationInMinutes })
  const availability = getAvailableBookingSlots().find((e) => {
    const availableStart = e.extendedProps.startLuxon
    const availableEnd = e.extendedProps.endLuxon
    const interval = Interval.fromDateTimes(availableStart, availableEnd)
    return (
      interval.contains(startLuxon) &&
      interval.length('minutes') >= durationInMinutes
    )
  })

  if (!availability) return null

  return availability.extendedProps.endLuxon >= endLuxon
    ? startLuxon
    : availability?.extendedProps.endLuxon.minus({
        minutes: durationInMinutes,
      })
}
export class CalendarEventStore {
  constructor({ feedUrl, overlayToggleInput }) {
    this.feedUrl = feedUrl
    this.overlayToggleInput = overlayToggleInput
    this.clearStore()
    this.calendar = new Promise((resolve) => {
      // bindCalendar is called when we've got reference to the calendar instance. Any fetchEvents call wait until we've got the reference.
      this.bindCalendar = resolve
    })
  }

  clearStore = () => {
    storeVersion += 1
    store.clear()
    pendingRequests.clear()
  }

  // This is what the fullcalendar api calls - see fullcalendar's api docs for events from function
  fetchEvents = async (fetchInfo) => {
    const calendar = await this.calendar
    const { start, end } = fetchInfo
    const startsAt = toLuxonDateTime(start, calendar).toUTC().toISO()
    const endsAt = toLuxonDateTime(end, calendar).toUTC().toISO()

    const events = await this.performFetch(startsAt, endsAt)

    return events
  }

  // If the month is not in the store, fetch it from the server and save it.
  // We also preprocess data returned from the server to make it fullcalendar compatible
  performFetch = async (startsAt, endsAt) => {
    const key = `${startsAt}-${endsAt}`
    if (store.has(key)) return store.get(key)
    if (pendingRequests.has(key)) return pendingRequests.get(key)

    const requestStoreVersion = storeVersion
    const request = (async () => {
      incrementPendingRequestCount()

      try {
        const requestUrl = new URL(this.feedUrl, window.location)
        requestUrl.searchParams.set('starts_at', startsAt)
        requestUrl.searchParams.set('ends_at', endsAt)
        requestUrl.searchParams.set(
          'overlay_calendars',
          this.overlayToggleInput?.checked
        )

        const form = document.querySelector(
          '[data-behavior*=refresh-calendar-on-change]'
        )
        const formData = new FormData(form || undefined)
        formData.delete('_method')
        appendCsrfParams(formData)

        const response = await fetch(requestUrl.toString(), {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/javascript' },
        })

        const result = this.processResponse(await response.json())
        if (requestStoreVersion === storeVersion) store.set(key, result)
        return result
      } finally {
        pendingRequests.delete(key)
        decrementPendingRequestCount()
      }
    })()

    pendingRequests.set(key, request)
    return request
  }

  processResponse = (response) => {
    // Availability is a list of time ranges where the user is available
    // We use fullcalendar's inverse-background event features to mark all times not covered by this range as unavailable

    const availableIntervals = response.available_intervals || []

    // Fullcalendar will consider everything as available if the availableIntervals array is empty and draw no diagonal lines. So we ensure there's always atleast one available interval - a fake one at the unix epoch. So the calendar is appropriately marked as unavailable.
    availableIntervals.push({
      starts_at: '1970-01-01T00:00:00.000Z',
      ends_at: '1970-01-01T00:00:01.000Z',
    })
    const availabilityEvents = (response.available_intervals || []).map(
      (availability) => {
        return {
          groupId: 'available',
          start: availability.starts_at,
          end: availability.ends_at,
          display: 'inverse-background',
          className: 'calendar-unavailable',
          allDay: false,
          extendedProps: {
            startLuxon: dateTime.fromISO(availability.starts_at),
            endLuxon: dateTime.fromISO(availability.ends_at),
          },
        }
      }
    )

    // Normal events shown on the calendar
    const events = response.events.map((event) => {
      return {
        groupId: 'event',
        title: event.title || '(No title)',
        ...event,
      }
    })

    return [...availabilityEvents, ...events]
  }
}
